import {
    fitImageSize,
    getOriginTransform,
    isUsableOriginRect,
    getVideoInfo,
    parseImageSize,
} from '@lightgallery/headless';

import { $LG, lgQuery } from './lgQuery';
import { VideoSource } from './plugins/video/types';
import { VideoInfo } from './types';

export interface ImageSize {
    width: number;
    height: number;
}

export interface ImageSources {
    media?: string;
    srcset: string;
    sizes?: string;
    type?: string;
}

export interface GalleryItem {
    /**
     * url of the media
     * @dataAttr data-src
     */
    src?: string;

    /**
     * Source attributes for the <a href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/source#attributes">picture</a> element
     * @dataAttr data-sources
     */
    sources?: ImageSources[];

    /**
     * Thumbnail url
     * @description By default lightGallery uses the image inside gallery selector as thumbnail.
     * But, If you want to use external image for thumbnail,
     * pass the thumbnail url via any data attribute and
     * pass the attribute name via exThumbImage option
     * @example
     * <div id="lightGallery">
     *     <a href="a.jpg" data-external-thumb-image="images/externalThumb.jpg" ><img src="thumb.jpg" /></a>
     * </div>
     *
     * lightGallery(document.getElementById('lightGallery'), {
     *     exThumbImage: 'data-external-thumb-image'
     * })
     * @dataAttr data-*
     */
    thumb?: string;

    /**
     * alt attribute for the image
     * @dataAttr alt
     */
    alt?: string;

    /**
     * Title attribute for the image or video. With the
     * getCaptionFromTitleOrAlt setting, it is used as the caption when
     * subHtml is not set.
     * @dataAttr title
     */
    title?: string;

    /**
     * Title for iframe
     * @dataAttr data-iframe-title
     */
    iframeTitle?: string;

    /**
     * Caption for the slide
     * @description You can either pass the HTML markup or the ID or class name of the element which contains the captions
     * @dataAttr data-sub-html
     */
    subHtml?: string;

    /**
     * url of the file which contain the sub html.
     * @description Note - Does not support Internet Explorer browser
     * @dataAttr data-sub-html-url
     */
    subHtmlUrl?: string;

    /**
     * Video source
     * @dataAttr data-video
     */
    video?: VideoSource;

    /**
     * Poster url
     * @dataAttr data-poster
     */
    poster?: string;

    /**
     * Custom slide name to use in the url when hash plugin is enabled
     * @dataAttr data-slide-name
     */
    slideName?: string;

    /**
     * List of images and viewport's max width separated by comma.
     * @description Ex?: img/1-375.jpg 375, img/1-480.jpg 480, img/1-757.jpg 757.
     * @dataAttr data-responsive
     */
    responsive?: string;

    /**
     * srcset attribute values for the main image
     * @dataAttr data-srcset
     */
    srcset?: string;

    /**
     * srcset sizes attribute for the main image
     * @dataAttr data-sizes
     */
    sizes?: string;

    /**
     * Set true is you want to open your url in an iframe
     * @dataAttr data-iframe
     */
    iframe?: boolean;

    /**
     * Download url for your image/video.
     * @description Pass false if you want to disable the download button.
     * @dataAttr data-download-url
     */
    downloadUrl?: string | boolean;

    /**
     * Name of the file after it is downloaded.
     * @description The HTML value of the download attribute.
     * There are no restrictions on allowed values, and the browser will automatically
     * detect the correct file extension and add it to the file (.img, .pdf, .txt, .html, etc.).
     * <a href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-download">More info</a>
     * @dataAttr data-download
     */
    download?: string | boolean;

    /**
     * Actual size of the image in px.
     * @description This is used in zoom plugin to see the actual size of the image when double-tapped on the image.
     * @dataAttr data-width
     */
    width?: string;

    /**
     * Canonical share URL for the native share sheet (Web Share API).
     * @description Specify only if you want a separate URL for the specific
     * slide. Falls back to the network-specific share URLs, then the current
     * browser URL.
     * @dataAttr data-share-url
     */
    shareUrl?: string;

    /**
     * Facebook share URL.
     * @description Specify only if you want to provide separate share URL for the specific slide. By default, current browser URL is taken.
     * @dataAttr data-facebook-share-url
     */
    facebookShareUrl?: string;

    /**
     * Tweet text
     * @dataAttr data-tweet-text
     */
    tweetText?: string;

    /**
     * Twitter share URL.
     * @description Specify only if you want to provide separate share URL for the specific slide. By default, current browser URL will be taken.
     * @dataAttr data-twitter-share-url
     */
    twitterShareUrl?: string;

    /**
     * Pinterest share URL.
     * @description Specify only if you want to provide separate share URL for the specific slide. By default, current browser URL will be taken.
     * Note?: Pinterest requires absolute URL
     * @dataAttr data-pinterest-share-url
     */
    pinterestShareUrl?: string;

    /**
     * Description for Pinterest post.
     * @dataAttr data-pinterest-text
     */
    pinterestText?: string;

    /**
     * Facebook comments body html
     * @description Please refer <a href="https://developers.facebook.com/docs/plugins/comments/#comments-plugin">facebook official documentation</a> for generating the HTML markup
     * @example
     * <div
     *      class="fb-comments"
     *      data-href="https://www.example.com/your-page/#lg=1&slide=0"
     *      data-width="400"
     *      data-numposts="5">
     * </div>
     * @dataAttr data-fb-html
     */
    fbHtml?: string;

    /**
     * Disqus page identifier
     * @description Please refer official <a href="https://help.disqus.com/en/articles/1717084-javascript-configuration-variables">disqus documentation</a> for more info
     * @dataAttr data-disqus-identifier
     */
    disqusIdentifier?: string;

    /**
     * Disqus page url
     * @description Please refer official <a href="https://help.disqus.com/en/articles/1717084-javascript-configuration-variables">disqus documentation</a> for more info
     * @dataAttr data-disqus-url
     */
    disqusUrl?: string;

    /**
     * Original size of the media as `"WIDTH-HEIGHT"`, for example `1600-1067`.
     * @description Drives the zoom-from-origin opening and closing animation
     * and the size of video slides. In a gallery built from markup it is read
     * from the item element. In dynamic mode set it on the item and pass the
     * element the slide should grow out of as the second argument of
     * `openGallery`; a `data-lg-size` attribute on that element still wins.
     * @dataAttr data-lg-size
     */
    lgSize?: string;

    __slideVideoInfo?: VideoInfo;
    [key: string]: any;
}

const defaultDynamicOptions = [
    'src',
    'sources',
    'subHtml',
    'subHtmlUrl',
    'html',
    'video',
    'poster',
    'slideName',
    'responsive',
    'srcset',
    'sizes',
    'iframe',
    'downloadUrl',
    'download',
    'width',
    'shareUrl',
    'facebookShareUrl',
    'tweetText',
    'iframeTitle',
    'twitterShareUrl',
    'pinterestShareUrl',
    'pinterestText',
    'fbHtml',
    'disqusIdentifier',
    'disqusUrl',
    'lgSize',
];

// Convert html data-attribute to camalcase
export function convertToData(attr: string): string {
    if (attr === 'href') {
        return 'src';
    }
    attr = attr.replace('data-', '');
    attr = attr.charAt(0).toLowerCase() + attr.slice(1);
    attr = attr.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

    return attr;
}

const utils = {
    /**
     * Fetches HTML content from a given URL and inserts it into a specified element.
     *
     * @param url - The URL to fetch the HTML content from.
     * @param element - The DOM element (jQuery object) to insert the HTML content into.
     * @param insertMethod - The method to insert the HTML ('append' or 'replace').
     */
    fetchCaptionFromUrl(
        url: string,
        element: lgQuery,
        insertMethod: 'append' | 'replace',
    ): void {
        // Fetch content from the URL
        fetch(url)
            .then((response) => response.text())
            .then((htmlContent) => {
                if (insertMethod === 'append') {
                    const contentDiv = `<div class="lg-sub-html">${htmlContent}</div>`;
                    element.append(contentDiv);
                } else {
                    element.html(htmlContent);
                }
            });
    },
    /**
     * Fit the media's original size into the container. The size comes from
     * the element's `data-lg-size` attribute, then the item's `lgSize`
     * field, then `defaultLgSize`. Used for the zoom-from-origin animation.
     */
    getSize(
        el: HTMLElement,
        container: lgQuery,
        spacing = 0,
        defaultLgSize?: string,
        itemLgSize?: string,
    ): ImageSize | undefined {
        const LGel = $LG(el);
        const lgSize = LGel.attr('data-lg-size') || itemLgSize || defaultLgSize;

        const parsed = parseImageSize(lgSize, window.innerWidth);
        if (!parsed) {
            return;
        }

        return fitImageSize(
            parsed,
            container.width(),
            container.height() - spacing,
        );
    },

    /**
     * @desc Get transform value based on the imageSize. Used for ZoomFromOrigin option
     * @param {jQuery Element}
     * @returns {String} Transform CSS string
     */
    getTransform(
        el: HTMLElement,
        container: lgQuery,
        top: number,
        bottom: number,
        imageSize?: ImageSize,
    ): string | undefined {
        // Degenerate measurement (zero-sized/hidden viewport, offsets
        // taller than the stage): the shared math would emit a mirrored
        // flight, fall back to the startClass fade instead (the sibling
        // bindings guard the same way).
        if (!imageSize || imageSize.width <= 0 || imageSize.height <= 0) {
            return;
        }
        // Measure the image inside the trigger, or the trigger itself when
        // it is the image (the framework packages use the same rule).
        const origin = $LG(el).find('img').first().get() || el;
        if (!origin) {
            return;
        }

        const containerRect = container.get().getBoundingClientRect();

        // Viewport coords straight from the rect. `offset()` folds in a
        // body-margin correction that shifts the origin on any centered
        // body (`margin: auto`), the long-standing "flight starts beside
        // the thumbnail" bug, and the old padding/border terms adjusted
        // in the wrong direction on styled thumbnails.
        const rect = origin.getBoundingClientRect();
        const triggerRect = {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
        };
        // A hidden or collapsed trigger (a collage's overflow items behind
        // a "+N photos" tile) measures 0×0 at the viewport origin: no
        // flight, the caller falls back to the centred animation.
        if (!isUsableOriginRect(triggerRect)) {
            return;
        }

        return getOriginTransform({
            triggerRect,
            containerRect: {
                left: containerRect.left,
                top: containerRect.top,
                width: containerRect.width,
                // Element height, not rect height, includes the mobile
                // safari bottom bar handling this always had.
                height: container.height(),
            },
            top,
            bottom,
            imageSize,
        });
    },

    getIframeMarkup(
        iframeWidth: string,
        iframeHeight: string,
        iframeMaxWidth: string,
        iframeMaxHeight: string,
        src?: string,
        iframeTitle?: string,
    ): string {
        const title = iframeTitle ? 'title="' + iframeTitle + '"' : '';
        return `<div class="lg-media-cont lg-has-iframe" style="width:${iframeWidth}; max-width:${iframeMaxWidth}; height: ${iframeHeight}; max-height:${iframeMaxHeight}">
                    <iframe class="lg-object" frameborder="0" ${title} src="${src}"  allowfullscreen="true"></iframe>
                </div>`;
    },

    getImgMarkup(
        index: number,
        src: string,
        altAttr: string,
        srcset?: string,
        sizes?: string,
        sources?: ImageSources[],
    ): string {
        const srcsetAttr = srcset ? `srcset="${srcset}"` : '';
        const sizesAttr = sizes ? `sizes="${sizes}"` : '';
        const imgMarkup = `<img ${altAttr} ${srcsetAttr}  ${sizesAttr} class="lg-object lg-image" data-index="${index}" src="${src}" />`;
        let sourceTag = '';
        if (sources) {
            const sourceObj =
                typeof sources === 'string' ? JSON.parse(sources) : sources;

            sourceTag = sourceObj.map((source: any) => {
                let attrs = '';
                Object.keys(source).forEach((key) => {
                    // Do not remove the first space as it is required to separate the attributes
                    attrs += ` ${key}="${source[key]}"`;
                });
                return `<source ${attrs}></source>`;
            });
        }
        return `${sourceTag}${imgMarkup}`;
    },

    // Get src from responsive src
    getResponsiveSrc(srcItms: string[]): string {
        const rsWidth = [];
        const rsSrc = [];
        let src = '';
        for (let i = 0; i < srcItms.length; i++) {
            const _src = srcItms[i].split(' ');

            // Manage empty space
            if (_src[0] === '') {
                _src.splice(0, 1);
            }

            rsSrc.push(_src[0]);
            rsWidth.push(_src[1]);
        }

        const wWidth = window.innerWidth;
        for (let j = 0; j < rsWidth.length; j++) {
            if (parseInt(rsWidth[j], 10) > wWidth) {
                src = rsSrc[j];
                break;
            }
        }
        return src;
    },

    isImageLoaded(img: HTMLImageElement): boolean {
        if (!img) return false;
        // During the onload event, IE correctly identifies any images that
        // weren’t downloaded as not complete. Others should too. Gecko-based
        // browsers act like NS4 in that they report this incorrectly.
        if (!img.complete) {
            return false;
        }

        // However, they do have two very useful properties: naturalWidth and
        // naturalHeight. These give the true size of the image. If it failed
        // to load, either of these should be zero.
        if (img.naturalWidth === 0) {
            return false;
        }

        // No other way of checking: assume it’s ok.
        return true;
    },

    getVideoPosterMarkup(
        _poster: string,
        dummyImg: string | HTMLElement,
        videoContStyle: string,
        playVideoString: string,
        _isVideo?: VideoInfo,
    ): string {
        let videoClass = '';
        if (_isVideo && _isVideo.youtube) {
            videoClass = 'lg-has-youtube';
        } else if (_isVideo && _isVideo.vimeo) {
            videoClass = 'lg-has-vimeo';
        } else if (_isVideo && _isVideo.wistia) {
            // Wistia fell into lg-has-html5 in 2.x; the facades made the
            // gap visible (binding providerClass had it right all along).
            videoClass = 'lg-has-wistia';
        } else {
            videoClass = 'lg-has-html5';
        }
        let _dummy = dummyImg;
        if (typeof dummyImg !== 'string') {
            _dummy = dummyImg.outerHTML;
        }
        return `<div class="lg-video-cont ${videoClass}" style="${videoContStyle}">
                <div class="lg-video-play-button">
                <svg
                    viewBox="0 0 20 20"
                    preserveAspectRatio="xMidYMid"
                    focusable="false"
                    aria-labelledby="${playVideoString}"
                    role="img"
                    class="lg-video-play-icon"
                >
                    <title>${playVideoString}</title>
                    <polygon class="lg-video-play-icon-inner" points="1,0 20,10 1,20"></polygon>
                </svg>
                <svg class="lg-video-play-icon-bg" viewBox="0 0 50 50" focusable="false">
                    <circle cx="50%" cy="50%" r="20"></circle></svg>
                <svg class="lg-video-play-icon-circle" viewBox="0 0 50 50" focusable="false">
                    <circle cx="50%" cy="50%" r="20"></circle>
                </svg>
            </div>
            ${_dummy}
            <img class="lg-object lg-video-poster" src="${_poster}" />
        </div>`;
    },

    getFocusableElements(container: HTMLElement): NodeListOf<Element> {
        const elements = container.querySelectorAll(
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
        );
        const visibleElements = [].filter.call(
            elements,
            (element: HTMLElement) => {
                const style = window.getComputedStyle(element);
                return (
                    style.display !== 'none' &&
                    style.visibility !== 'hidden' &&
                    // Hidden or moved into the toolbar's More menu, whatever
                    // the stylesheet says about their display.
                    !element.closest('[hidden], [data-lg-overflow]')
                );
            },
        );
        return visibleElements as unknown as NodeListOf<Element>;
    },

    /**
     * @desc Create dynamic elements array from gallery items when dynamic option is false
     * It helps to avoid frequent DOM interaction
     * and avoid multiple checks for dynamic elments
     *
     * @returns {Array} dynamicEl
     */
    getDynamicOptions(
        items: any,
        extraProps: string[],
        getCaptionFromTitleOrAlt: boolean,
        exThumbImage: string,
    ): GalleryItem[] {
        const dynamicElements: GalleryItem[] = [];
        const availableDynamicOptions = [
            ...defaultDynamicOptions,
            ...extraProps,
        ];
        [].forEach.call(items, (item: HTMLElement) => {
            const dynamicEl: GalleryItem = {} as GalleryItem;
            for (let i = 0; i < item.attributes.length; i++) {
                const attr = item.attributes[i];
                if (attr.specified) {
                    const dynamicAttr = convertToData(attr.name);
                    let label = '';
                    if (availableDynamicOptions.indexOf(dynamicAttr) > -1) {
                        label = dynamicAttr;
                    }
                    if (label) {
                        (dynamicEl as any)[label] = attr.value;
                    }
                }
            }
            // `data-sources` is JSON on the element; the item carries the
            // parsed array so every consumer (slide markup, the zoom
            // plugin's actual-size lookup) sees the headless shape.
            if (typeof dynamicEl.sources === 'string') {
                try {
                    dynamicEl.sources = JSON.parse(dynamicEl.sources);
                } catch (e) {
                    console.warn(
                        'lightGallery :- data-sources must be a JSON array of picture source objects. See https://www.lightgalleryjs.com/docs/responsive-loading/',
                    );
                    delete dynamicEl.sources;
                }
            }
            const currentItem = $LG(item);
            const alt = currentItem.find('img').first().attr('alt');
            const title = currentItem.attr('title');

            const thumb = exThumbImage
                ? currentItem.attr(exThumbImage)
                : currentItem.find('img').first().attr('src');
            dynamicEl.thumb = thumb;

            if (getCaptionFromTitleOrAlt && !dynamicEl.subHtml) {
                dynamicEl.subHtml = title || alt || '';
            }
            dynamicEl.alt = alt || title || '';
            dynamicElements.push(dynamicEl);
        });
        return dynamicElements;
    },
    isMobile(): boolean {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    },
    /**
     * @desc Check the given src is video
     * @param {String} src
     * @return {Object} video type
     * Ex:{ youtube  :  ["//www.youtube.com/watch?v=c0asJgSyxcY", "c0asJgSyxcY"] }
     *
     * @todo - this information can be moved to dynamicEl to avoid frequent calls
     */

    isVideo(
        src: string,
        isHTML5VIdeo: boolean,
        index: number,
    ): VideoInfo | undefined {
        if (!src) {
            if (isHTML5VIdeo) {
                return {
                    html5: true,
                };
            } else {
                console.error(
                    'lightGallery :- data-src is not provided on slide item ' +
                        (index + 1) +
                        '. Please make sure the selector property is properly configured. More info - https://www.lightgalleryjs.com/demos/html-markup/',
                );
                return;
            }
        }

        // Documented deviation from the shared helper: 2.x ignores the
        // html5 flag once a src is present, a URL matching no provider
        // is not a video, video payload or not.
        return getVideoInfo(src, false);
    },
};

export default utils;
